<template>
  <b-modal
    ref="add-manual-point-popup"
    hide-footer
    hide-header
    centered
    id="add-manual-point-popup"
    no-close-on-backdrop
    no-close-on-esc
  >
    <div>
      <h3 class="popup-title">
        {{ title }}
        <button
          type="button"
          class="close"
          @click="handleCancelButton"
          v-if="!successData"
        >
          ×
        </button>
      </h3>
      <hr>
      <form
        class="form-group needs-validation"
        @submit.prevent="handleSubmit"
      >
        <div class="input-style form-inline">
          <div
            v-if="!successData"
          >
            <span v-html="$t('giveExtraPointsTo', { playerName: data.name })"></span>
          </div>
          <div v-else class="text-center col">
            <div>
              <span
                v-html="$t('extraPointGivenTo',
                           { addedPoint: successData.addedPoint, playerName: data.name })"
                v-if="isSingularPointText(point)"
              ></span>
              <span
                v-html="$t('extraPointsGivenTo',
                           { addedPoint: successData.addedPoint, playerName: data.name })"
                v-else
              ></span>
            </div>
            <div>
              <span>{{ $t('New score:') }} {{ successData.newPoint }}.</span>
              <span>&nbsp;</span>
              <span>{{ $t('New ranking:') }} {{ successData.newRanking }}.</span>
            </div>
          </div>
          <input
            type="number"
            spellcheck="false"
            id="point-adding"
            class="ml-2 form-control"
            v-model="point"
            ref="inputStyle"
            v-if="!successData"
            required
          >
        </div>

        <div class="pt-2 text-left text-danger m-b-10">
          {{ textError }}
        </div>

        <div
          class="pt-2 text-center"
          v-if="!successData"
        >
          <button
            type="submit"
            class="btn btn-primary m-r-10 confirm-modal-ok-btn"
            :disabled="isAddingPoint"
          >
            {{ $t('OK') }} <span class="loading-spinner" v-if="isAddingPoint" />
          </button>
          <button
            type="button"
            class="btn btn-light"
            @click="handleCancelButton"
          >
            {{ $t('Cancel') }}
          </button>
        </div>
        <div
          class="pt-2 text-center"
          v-else
        >
          <button
            type="submit"
            ref="doneButton"
            class="btn btn-primary m-r-10 confirm-modal-ok-btn"
          >
            {{ $t('Done') }}
          </button>
        </div>
      </form>
    </div>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Util from '@/util'

const maxPoint = 1000

export default {
  name: 'AddNewPointPopup',
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    audiences: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isFocus: false,
      point: 0,
      title: `${this.$t('Score adjustment')}`,
      textError: '',
      successData: null,
    }
  },
  computed: {
    ...mapGetters('leaderboard', [
      'isAddingPoint',
    ]),
  },
  mounted() {
    this.$refs['add-manual-point-popup'].show()
    this.updateSlideActiveAction({
      visiblePopup: true,
    })
    this.isFocus = true
    this.point = this.data.manualPoint
    setTimeout(() => {
      this.$refs.inputStyle.focus()
    }, 100)
  },
  methods: {
    ...mapActions(['updateSlideActiveAction']),
    ...mapActions('leaderboard', [
      'addPointAction',
    ]),
    async handleSubmit() {
      if (this.successData) {
        this.handleDone()
      } else {
        await this.handleConfirmBtn()
      }
    },
    async handleConfirmBtn() {
      this.textError = ''
      this.point = parseInt(this.point, 10)

      if (this.point === this.data.manualPoint) {
        this.handleClose()
        return
      }

      if (this.point > maxPoint) {
        this.textError = `The max amount of points that can be given is ${Util.numberWithCommas(maxPoint, 'en')}. Please try again.`
        return
      }

      const currentPoint = this.data.newPoint + this.data.oldPoint
      if (this.point < 0 && this.point < -currentPoint) {
        this.textError = 'You can only deduct as many points as the player is having. Please try again.'
        return
      }

      await this.addPoint()
    },

    async addPoint() {
      try {
        const { slideId } = this.data
        const result = await this.addPointAction({
          teamId: this.data.teamId,
          audienceId: this.data.audienceId,
          presentationId: this.data.presentationId,
          slideId,
          point: this.point,
        })
        const addedPoint = result.point
        const newPoint = this.data.totalPoint + addedPoint
        const ranking = this.audiences
          .filter((a) => a.totalPoint > newPoint && (a.id || a.audience) !== (this.data.id || this.data.audience))
          .length + 1
        this.successData = {
          addedPoint,
          newPoint,
          newRanking: `${ranking}${Util.ordinalSuffixOf(ranking)}`,
        }
        this.$emit('ok')

        setTimeout(() => {
          this.$refs.doneButton.focus()
        }, 1)
      } catch (error) {
        this.handleError(error, 'Submission failed. Please try again.')
        throw error
      }
    },

    isSingularPointText(point) {
      return Math.abs(point) <= 1
    },

    handleError(error, text) {
      this.isAddingPoint = false
      this.textError = (error.body && error.body.message) || text
    },

    handleCancelButton() {
      Util.mixpanelTracking(
        'Presentation Editor',
        'Leaderboard > Add manual point > Cancel adding point',
        `Point would add: ${this.point}`,
      )
      this.handleClose()
    },

    handleClose() {
      this.$refs['add-manual-point-popup'].hide()
      this.updateSlideActiveAction({
        visiblePopup: false,
      })
      this.$emit('close')
    },

    handleDone() {
      this.$refs['add-manual-point-popup'].hide()
      this.updateSlideActiveAction({
        visiblePopup: false,
      })
      this.$emit('close')
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variable.scss';
.popup-title {
  word-break: break-word;
  text-align: left;
  font-weight: bold;
  font-size: 18px;
  margin-bottom: 16px;
}
.input-style {
  position: relative;
  -webkit-box-flex: 1;
  -ms-flex: 1 1 auto;
  flex: 1 1 auto;

  input {
    max-width: 7rem;
  }
}

#add-manual-point-popup___BV_modal_outer_ {
  z-index: 9999999999999 !important;
}
</style>
